import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Users } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { SharedGroupsSheet } from "@/components/SharedGroupsSheet";

// Lot 746 — ligne « partagé dans N groupes : A, B, C, +k » sous l'auteur d'une carte du fil.
// Carte de partage (post.group) : « a partagé dans 4 groupes : … » / « et 2 autre(s) ont partagé dans 6 groupes : … ».
// Carte de l'original (post.group_shares sans post.group) : « Partagé [par X] dans 4 groupes : … ».
// Lot 747 — compteur serveur `group_share_count` (tous les groupes, membre ou non) sur l'original.
// Lot 749 — le libellé « Partagé dans N groupes » est tapable → feuille des groupes (publics + mes privés) : rejoindre / ouvrir.
// Lot 750 — « Retirer » dans la feuille : les groupes où plus personne n'a partagé sortent de la ligne (`gone`), compteur ajusté.
// Sans group_shares → libellé historique « dans le groupe X ». Chaque nom ouvre le groupe.
const SHOWN = 3;

const GroupLink = ({ g, postId }) => (
  <Link to={`/groups/${g.id}`} className="hover:underline inline-flex items-center gap-0.5" data-testid={`post-group-link-${postId}-${g.id}`}>
    {g.privacy === "private" ? <Lock size={10} /> : null}{g.name}
  </Link>
);

export const GroupSharesLine = ({ post }) => {
  const { t } = useApp();
  const [all, setAll] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [gone, setGone] = useState([]);
  const onRemoved = (gid, r) => { if (!r?.still_shared) setGone((g) => (g.includes(gid) ? g : [...g, gid])); };
  const gs = post.group_shares && gone.length ? { ...post.group_shares, groups: post.group_shares.groups.filter((g) => !gone.includes(g.id)) } : post.group_shares;
  const style = { color: "var(--yalola-primary)" };
  const serverCount = Math.max(0, Number(post.group_share_count || 0) - gone.length);
  const origId = post.shared_post?.id || post.id;
  const leadBtn = (children, testId) => (
    <button type="button" onClick={() => setSheet(true)} className="hover:underline text-left" title={t("post.gs_tap_hint", "Voir les groupes")} data-testid={testId}>{children}</button>
  );
  if (!gs || !gs.groups?.length) {
    if (post.group && !gone.includes(post.group.id)) {
      return (
        <Link to={`/groups/${post.group.id}`} className="text-[11px] font-semibold inline-flex items-center gap-1 hover:underline mt-0.5" style={style} data-testid={`post-group-label-${post.id}`}>
          {post.group.privacy === "private" ? <Lock size={11} /> : <Users size={11} />} {t("post.in_group", "dans le groupe")} {post.group.name}
        </Link>
      );
    }
    if (serverCount > 0 && !post.shared_post) {
      return (
        <div className="text-[11px] font-semibold inline-flex items-center gap-1 mt-0.5" style={style} data-testid={`post-group-share-count-${post.id}`} data-count={serverCount}>
          <Users size={11} />
          {leadBtn(serverCount === 1 ? t("post.gs_count_one", "Partagé dans 1 groupe") : t("post.gs_count", "Partagé dans {n} groupes").replace("{n}", String(serverCount)), `post-group-shares-open-${post.id}`)}
          <SharedGroupsSheet postId={origId} open={sheet} onOpenChange={setSheet} onRemoved={onRemoved} />
        </div>
      );
    }
    return null;
  }
  const n = post.group ? gs.groups.length : Math.max(gs.groups.length, serverCount);
  const others = gs.sharers.filter((s) => s.id !== post.author?.id);
  const who = others.length ? (others.length > 1 ? t("post.gs_others", "{name} et {m} autre(s)").replace("{name}", others[0].name).replace("{m}", String(others.length - 1)) : others[0].name) : "";
  // Lot 755 — doublons écrits dans plusieurs groupes (pas des partages) : « dans N groupes : … », sans feuille de partage.
  const dup = gs.kind === "dup";
  let lead;
  if (dup) {
    lead = n === 1 ? t("post.in_group", "dans le groupe") : t("post.gs_dup_many", "dans {n} groupes :").replace("{n}", String(n));
  } else if (post.group) {
    if (n === 1 && !others.length) lead = t("post.in_group", "dans le groupe");
    else if (!others.length) lead = t("post.gs_share_many", "a partagé dans {n} groupes :").replace("{n}", String(n));
    else lead = t("post.gs_share_many_others", "et {m} autre(s) ont partagé dans {n} groupes :").replace("{m}", String(others.length)).replace("{n}", String(n));
  } else if (n === 1) {
    lead = who ? t("post.gs_orig_one_by", "Partagé par {who} dans le groupe").replace("{who}", who) : t("post.gs_orig_one", "Partagé dans le groupe");
  } else {
    lead = who ? t("post.gs_orig_many_by", "Partagé par {who} dans {n} groupes :").replace("{who}", who).replace("{n}", String(n)) : t("post.gs_orig_many", "Partagé dans {n} groupes :").replace("{n}", String(n));
  }
  const shown = all ? gs.groups : gs.groups.slice(0, SHOWN);
  const rest = gs.groups.length - shown.length;
  return (
    <div className="text-[11px] font-semibold flex flex-wrap items-center gap-x-1 mt-0.5" style={style} data-testid={`post-group-shares-${post.id}`} data-count={n} data-kind={dup ? "dup" : "share"}>
      <Users size={11} /> {dup ? <span data-testid={`post-group-shares-lead-${post.id}`}>{lead}</span> : leadBtn(lead, `post-group-shares-open-${post.id}`)}
      {shown.map((g, i) => (
        <span key={g.id}><GroupLink g={g} postId={post.id} />{i < shown.length - 1 || rest > 0 ? "," : ""}</span>
      ))}
      {rest > 0 && (
        <button type="button" onClick={() => setAll(true)} className="rounded-full px-1.5 hover:underline" style={{ background: "var(--yalola-primary-soft)" }} data-testid={`post-group-shares-more-${post.id}`}>+{rest}</button>
      )}
      {!dup && <SharedGroupsSheet postId={origId} open={sheet} onOpenChange={setSheet} onRemoved={onRemoved} />}
    </div>
  );
};
